import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';

// Public pages - Client site
import ClientHomePage from './pages/ClientHomePage';
import CGVPage from './pages/CGVPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import EvaluationPage from './pages/EvaluationPage';
import ClientMissionPage from './pages/ClientMissionPage';
import ClientPresentationPage from './pages/ClientPresentationPage';
import CandidateOnboardingPage from './pages/CandidateOnboardingPage';

// Public pages - Candidate portal
import ApplyPage from './pages/ApplyPage';
import PostulerLandingPage from './pages/PostulerLandingPage';
import ThankYouPage from './pages/ThankYouPage';
import PublicQualificationPage from './pages/PublicQualificationPage';
import ApplyWithTokenPage from './pages/ApplyWithTokenPage';
import QualificationTokenPage from './pages/QualificationTokenPage';

// Admin pages
import AdminLayout from './layouts/AdminLayout';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import LeadsPage from './pages/admin/LeadsPage';
import APIIntegrationPage from './pages/admin/APIIntegrationPage';
import CandidatesPage from './pages/admin/CandidatesPage';
import RejectedCandidatesPage from './pages/admin/RejectedCandidatesPage';
import CandidateProfilePage from './pages/admin/CandidateProfilePage';
import MerciPage from './pages/MerciPage';
import TalentPoolPage from './pages/admin/TalentPoolPage';
import EmployeeProfilePage from './pages/admin/EmployeeProfilePage';
import ClientsPage from './pages/admin/ClientsPage';
import AddClientPage from './pages/admin/AddClientPage';
import ClientProfilePage from './pages/admin/ClientProfilePage';
import MissionsPage from './pages/admin/MissionsPage';
import MissionDetailsPage from './pages/admin/MissionDetailsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import UsersPage from './pages/admin/UsersPage';
import QualificationDashboardPage from './pages/admin/QualificationDashboardPage';
import QualificationFormPage from './pages/admin/QualificationFormPage';
import BrandsPage from './pages/admin/BrandsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import ConversionDashboardPage from './pages/admin/ConversionDashboardPage';
import CategoryAnalyticsPage from './pages/admin/CategoryAnalyticsPage';
import StrategicViewPage from './pages/admin/StrategicViewPage';
import AssignmentHistoryPage from './pages/admin/AssignmentHistoryPage';
import TrackingPage from './pages/admin/TrackingPage';
import MessagesPage from './pages/admin/MessagesPage';
import CommunicationDashboardPage from './pages/admin/CommunicationDashboardPage';
import CalendarPage from './pages/admin/CalendarPage';
import RecruterSuissePage from './pages/RecruterSuissePage';

import './App.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster />
        <Routes>
          {/* Client-facing site (Madagascar focus) */}
          <Route path="/" element={<ClientHomePage />} />
          
          {/* Legal pages */}
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          
          {/* Evaluation page (for clients) */}
          <Route path="/evaluer/:token" element={<EvaluationPage />} />
          
          {/* Client Mission Decision Portal (secured via token) */}
          <Route path="/client/m/:token" element={<ClientMissionPage />} />
          <Route path="/client/talents/:token" element={<ClientPresentationPage />} />
          <Route path="/onboarding/:token" element={<CandidateOnboardingPage />} />
          
          {/* Candidate application portal */}
          <Route path="/postuler" element={<PostulerLandingPage />} />
          <Route path="/postuler/formulaire" element={<ApplyPage />} />
          <Route path="/postuler/merci" element={<ThankYouPage />} />

          {/* Landing Suisse — page de conversion B2B dédiée */}
          <Route path="/recruter-suisse" element={<RecruterSuissePage />} />

          {/* Page de remerciement universelle (homepage form + Suisse landing) */}
          <Route path="/merci" element={<MerciPage />} />
          
          {/* Public qualification forms (no login required) */}
          <Route path="/evaluation/:formType" element={<PublicQualificationPage />} />
          
          {/* Unique qualification links with tokens */}
          <Route path="/apply/:role/:token" element={<ApplyWithTokenPage />} />
          <Route path="/qualification/:token" element={<QualificationTokenPage />} />
          
          {/* Admin login */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          
          {/* Protected admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="api-integration" element={<APIIntegrationPage />} />
            <Route path="candidates" element={<CandidatesPage />} />
            <Route path="candidates-rejetes" element={<RejectedCandidatesPage />} />
            <Route path="candidates/:candidateId" element={<CandidateProfilePage />} />
            <Route path="talent-pool" element={<TalentPoolPage />} />
            <Route path="talent-pool/:employeeId" element={<EmployeeProfilePage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/new" element={<AddClientPage />} />
            <Route path="clients/:clientId" element={<ClientProfilePage />} />
            <Route path="missions" element={<MissionsPage />} />
            <Route path="missions/:missionId" element={<MissionDetailsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="brands" element={<BrandsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="conversion" element={<ConversionDashboardPage />} />
            <Route path="analytics/categories" element={<CategoryAnalyticsPage />} />
            <Route path="strategic-view" element={<StrategicViewPage />} />
            <Route path="assignment-history" element={<AssignmentHistoryPage />} />
            <Route path="tracking" element={<TrackingPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="communications" element={<CommunicationDashboardPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="qualifications" element={<QualificationDashboardPage />} />
            <Route path="qualify/:candidateId/:profileType" element={<QualificationFormPage />} />
          </Route>
        </Routes>
        
        {/* PWA Update Prompt - Shows when new version available */}
        <PWAUpdatePrompt />
      </AuthProvider>
    </Router>
  );
}

export default App;